/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewEncapsulation, inject } from '@angular/core';
import { NavBarLinkRef } from '@alfresco/adf-extensions';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { IconComponent } from '@alfresco/adf-core';
import { MatButtonModule } from '@angular/material/button';
import { ActiveLinkDirective } from '../directives/active-link.directive';
import { ActionDirective } from '../directives/action.directive';
import { MatExpansionModule } from '@angular/material/expansion';

@Component({
  imports: [CommonModule, TranslatePipe, IconComponent, MatButtonModule, ActiveLinkDirective, ActionDirective, MatExpansionModule],
  selector: 'app-expand-menu',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './expand-menu.component.html',
  host: { class: 'app-expand-menu' }
})
export class ExpandMenuComponent implements OnInit {
  private readonly cd = inject(ChangeDetectorRef);
  private readonly el = inject(ElementRef);

  @Input({ required: true })
  item: NavBarLinkRef;

  @Output()
  actionClicked = new EventEmitter<NavBarLinkRef>();

  /**
   * id of the sub-menu child item currently under the pointer.
   * The forced-open `mat-expansion-panel` (see `_modern.scss`) makes the panel —
   * not the child `.aca-action-button`s painted inside it — the mouse hit-test
   * target, so per-child `:hover` never fires and items can't be highlighted with
   * CSS alone. We instead track the hovered child by pointer position on the panel
   * (which does receive mouse events) and drive the highlight via `.aca-hovered`.
   */
  hoveredChildId: string | null = null;

  ngOnInit() {
    this.cd.detectChanges();
  }

  onPanelPointerMove(event: MouseEvent): void {
    const buttons: NodeListOf<HTMLElement> = this.el.nativeElement.querySelectorAll('.aca-expansion-panel-body .aca-action-button');
    let found: string | null = null;
    buttons.forEach((button) => {
      const rect = button.getBoundingClientRect();
      if (event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom) {
        found = button.id || null;
      }
    });
    if (found !== this.hoveredChildId) {
      this.hoveredChildId = found;
    }
  }

  clearHoveredChild(): void {
    this.hoveredChildId = null;
  }

  trackById(_index: number, obj: NavBarLinkRef) {
    return obj.id;
  }
}
